/**
 * stdb_socket — the SpacetimeDB connection's socket, held by a worker (the loading-screen reset, 2026-10-05).
 *
 * The WHY is in lib/stdb_socket_core.ts: a frozen page frame (22–40 s at high settings during the boot) stopped the
 * page draining its socket, the server's pings went unanswered, SpacetimeDB timed the client out after 30 s and the
 * reconnect law reloaded the page — the loading screen started over. Here the socket lives in
 * `workers/stdb_socket_worker.ts`, which keeps reading it while the page is busy; a frame the page could not take while
 * frozen waits in the worker's message queue and is handed on, in order, once the page is free.
 *
 * `openStdbSocket` is a `WebSocketFactory` for `DbConnection.builder().withWSFn(…)`. It MIRRORS the SDK's
 * `openWebSocket` (sdk/ws.ts: the short-lived websocket token, the subscribe URL and its query); `verify:stdb-socket`
 * pins the mirror against the SDK's own source, so an SDK upgrade that changes that contract fails the gate. Where no
 * worker can start, the same core runs in the page (the old behaviour, same decoding). `localStorage atlas:ws-worker =
 * 'off'` (or `?wsWorker=off`) keeps the SDK's own socket. Lever: `__stdbSocket()`.
 */
import { SocketCore, type CoreIn, type CoreOut, type SocketCtor } from '../lib/stdb_socket_core.js';

/** The SDK's `WebSocketArgs` (sdk/ws.d.ts — not exported by the package, so restated). */
export interface StdbSocketArgs {
  url: URL;
  wsProtocol: string[];
  nameOrAddress: string;
  authToken?: string;
  compression: 'gzip' | 'brotli' | 'none';
  lightMode: boolean;
  confirmedReads?: boolean;
}

/** worker = the socket on its own thread (default); sdk = the SDK's own in-page socket (the kill switch). */
export function stdbSocketMode(
  store: Pick<Storage, 'getItem'> | null = typeof localStorage !== 'undefined' ? localStorage : null,
  search: string = typeof location !== 'undefined' ? location.search : '',
): 'worker' | 'sdk' {
  try {
    if (/[?&]wsWorker=off\b/.test(search)) return 'sdk';
    if (store?.getItem('atlas:ws-worker') === 'off') return 'sdk';
  } catch { /* storage blocked — the default */ }
  return typeof Worker !== 'undefined' ? 'worker' : 'sdk';
}

// ── The SDK's openWebSocket, mirrored ────────────────────────────────────────────────────────────────────────────────

const COMPRESSION_PARAM = { gzip: 'Gzip', brotli: 'Brotli', none: 'None' } as const;

/** Where the long-lived token is exchanged for the socket's short-lived one (the SDK's `tokenUrl`). */
export function wsTokenUrl(url: URL): URL {
  const t = new URL('v1/identity/websocket-token', url);
  t.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
  return t;
}

/** The subscribe URL exactly as the SDK builds it (`databaseUrl`). */
export function subscribeUrl(
  a: Pick<StdbSocketArgs, 'url' | 'nameOrAddress' | 'compression' | 'lightMode' | 'confirmedReads'>,
  temporaryAuthToken?: string,
): string {
  const u = new URL(`v1/database/${a.nameOrAddress}/subscribe`, a.url);
  if (temporaryAuthToken) u.searchParams.set('token', temporaryAuthToken);
  u.searchParams.set('compression', COMPRESSION_PARAM[a.compression] ?? 'None');
  if (a.lightMode) u.searchParams.set('light', 'true');
  if (a.confirmedReads !== undefined) u.searchParams.set('confirmed', a.confirmedReads.toString());
  return u.toString();
}

/** POST the long-lived token for a short-lived socket token; the SDK's error text on a refusal (the login reads it). */
export async function fetchWsToken(url: URL, authToken: string, fetchFn: typeof fetch = fetch): Promise<string> {
  const response = await fetchFn(wsTokenUrl(url), { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
  if (!response.ok) throw new Error(`Failed to verify token: ${response.statusText}`);
  const { token } = (await response.json()) as { token: string };
  return token;
}

// ── The adapter the SDK talks to ─────────────────────────────────────────────────────────────────────────────────────

interface Host { post(msg: CoreIn, transfer?: ArrayBuffer[]): void; terminate(): void }

export interface StdbSocketDeps {
  /** A worker running workers/stdb_socket_worker.ts, or null when none can start. */
  makeWorker: () => Worker | null;
  /** The page's WebSocket, for the in-page fallback. */
  PageSocket: SocketCtor | null;
  /** Absolute ms (`timeOrigin + now()`), the same clock the worker stamps frames with. */
  now: () => number;
}

const defaultDeps = (): StdbSocketDeps => ({
  makeWorker: () => {
    try {
      return new Worker(new URL('../workers/stdb_socket_worker.ts', import.meta.url), { type: 'module', name: 'stdb-socket' });
    } catch { return null; }
  },
  PageSocket: typeof WebSocket !== 'undefined' ? (WebSocket as unknown as SocketCtor) : null,
  now: () => performance.timeOrigin + performance.now(),
});

/** A frame handed on later than this was held in the worker while the page was frozen — worth a line in the console. */
export const FROZEN_NOTE_MS = 10_000;

export interface StdbSocketStats {
  mode: 'worker' | 'page';
  state: 'connecting' | 'open' | 'closing' | 'closed';
  msgs: number;
  bytes: number;
  /** How late a frame reached the SDK after it arrived (ms): the page's freeze the worker absorbed. */
  lastLagMs: number;
  maxLagMs: number;
  /** Frames handed on more than FROZEN_NOTE_MS late. */
  heldThroughFreeze: number;
  closeCode: number | null;
  fellBack: boolean;
}

type Kind = 'open' | 'message' | 'error' | 'close';
type AnyHandler = (ev: never) => void;

let current: StdbSocketAdapter | null = null;

export class StdbSocketAdapter {
  protocol = '';
  readyState = 0;
  readonly stats: StdbSocketStats;
  private host: Host;
  private handlers: Partial<Record<Kind, AnyHandler>> = {};
  /** Events that arrived before the SDK set all four handlers (it sets them together, so this is a safety net). */
  private queued: Array<[Kind, unknown]> = [];
  private noteAt = 0;

  constructor(private readonly url: string, private readonly protocols: string[], private readonly deps: StdbSocketDeps) {
    this.stats = { mode: 'worker', state: 'connecting', msgs: 0, bytes: 0, lastLagMs: 0, maxLagMs: 0, heldThroughFreeze: 0, closeCode: null, fellBack: false };
    this.host = this.startWorker() ?? this.startPage();
    current = this;
    this.publish();
  }

  // The SDK's WebSocketAdapter surface.
  set onopen(h: () => void) { this.setHandler('open', h as AnyHandler); }
  set onmessage(h: (msg: { data: Uint8Array }) => void) { this.setHandler('message', h as AnyHandler); }
  set onerror(h: (ev: ErrorEvent) => void) { this.setHandler('error', h as AnyHandler); }
  set onclose(h: (ev: CloseEvent) => void) { this.setHandler('close', h as AnyHandler); }

  send(msg: Uint8Array): void {
    if (this.readyState !== 1) return;
    // Copy the view's bytes only: the SDK's writer may hand a view into a larger, reused buffer.
    const copy = msg.slice();
    this.host.post({ type: 'send', data: copy.buffer as ArrayBuffer }, [copy.buffer as ArrayBuffer]);
  }

  close(): void {
    if (this.readyState >= 2) return;
    this.readyState = 2;
    this.stats.state = 'closing';
    this.host.post({ type: 'close' });
    this.publish();
  }

  // ── hosts ──

  private startWorker(): Host | null {
    const w = this.deps.makeWorker();
    if (!w) return null;
    let opened = false;
    w.onmessage = (e: MessageEvent<CoreOut>) => {
      if (e.data.type === 'open') opened = true;
      this.receive(e.data);
    };
    const fail = (why: string): void => {
      if (this.readyState === 3) return;
      if (!opened) {
        // The worker never got going (its script failed to load, or a CSP refused it): run the socket in the page.
        console.warn(`[stdb-socket] the socket worker failed (${why}) — running the socket on the page`);
        try { w.terminate(); } catch { /* gone */ }
        this.stats.fellBack = true;
        this.host = this.startPage();
        return;
      }
      // The worker died with the socket open: the socket is gone — the reconnect law takes it from here.
      this.receive({ type: 'close', code: 1006, reason: `socket worker failed: ${why}`, wasClean: false });
    };
    w.onerror = (e: ErrorEvent) => { e.preventDefault?.(); fail(e.message || 'error'); };
    w.onmessageerror = () => fail('messageerror');
    w.postMessage({ type: 'open', url: this.url, protocols: this.protocols } satisfies CoreIn);
    this.stats.mode = 'worker';
    return { post: (msg, transfer) => w.postMessage(msg, transfer ?? []), terminate: () => w.terminate() };
  }

  private startPage(): Host {
    this.stats.mode = 'page';
    const Ctor = this.deps.PageSocket;
    if (!Ctor) {
      queueMicrotask(() => this.receive({ type: 'close', code: 1006, reason: 'no WebSocket', wasClean: false }));
      return { post: () => {}, terminate: () => {} };
    }
    const core = new SocketCore(Ctor, (m) => this.receive(m), this.deps.now);
    core.handle({ type: 'open', url: this.url, protocols: this.protocols });
    return { post: (m) => core.handle(m), terminate: () => {} };
  }

  // ── events ──

  /** Exposed for the harness (a fake host); the hosts call it. */
  receive(m: CoreOut): void {
    switch (m.type) {
      case 'open':
        this.readyState = 1;
        this.protocol = m.protocol;
        this.stats.state = 'open';
        this.fire('open', undefined);
        break;
      case 'message': {
        const lag = Math.max(0, this.deps.now() - m.at);
        const s = this.stats;
        s.msgs++; s.bytes += m.len; s.lastLagMs = lag;
        if (lag > s.maxLagMs) s.maxLagMs = lag;
        if (lag > FROZEN_NOTE_MS) {
          s.heldThroughFreeze++;
          const t = this.deps.now();
          if (t - this.noteAt > FROZEN_NOTE_MS) {
            this.noteAt = t;
            console.info(`[stdb-socket] the page was frozen ~${(lag / 1000).toFixed(0)} s; the socket worker kept the connection alive`);
          }
        }
        this.fire('message', { data: new Uint8Array(m.buf, m.off, m.len) });
        break;
      }
      case 'error':
        if (m.decode) { console.error(`[stdb-socket] ${m.message}`); break; } // the close that follows reports it
        this.fire('error', makeErrorEvent(m.message));
        break;
      case 'close':
        if (this.readyState === 3) break;
        this.readyState = 3;
        this.stats.state = 'closed';
        this.stats.closeCode = m.code;
        try { this.host.terminate(); } catch { /* gone */ }
        this.fire('close', makeCloseEvent(m.code, m.reason, m.wasClean));
        break;
    }
    this.publish();
  }

  private setHandler(kind: Kind, h: AnyHandler): void {
    this.handlers[kind] = h;
    if (this.queued.length && this.allSet()) {
      const q = this.queued.splice(0);
      for (const [k, ev] of q) this.call(k, ev);
    }
  }

  private allSet(): boolean {
    const h = this.handlers;
    return !!(h.open && h.message && h.error && h.close);
  }

  private fire(kind: Kind, ev: unknown): void {
    if (!this.allSet() || this.queued.length) { this.queued.push([kind, ev]); return; }
    this.call(kind, ev);
  }

  private call(kind: Kind, ev: unknown): void {
    (this.handlers[kind] as ((e: unknown) => void) | undefined)?.(ev);
  }

  /** For the dev beacon in index.html (its WebSocket wrapper cannot see a worker's socket). */
  private publish(): void {
    try {
      (globalThis as Record<string, unknown>).__atlasStdbSocket = {
        state: this.stats.state, code: this.stats.closeCode, mode: this.stats.mode, url: this.url.split('?')[0].slice(0, 120),
      };
    } catch { /* no global */ }
  }
}

function makeCloseEvent(code: number, reason: string, wasClean: boolean): CloseEvent {
  if (typeof CloseEvent !== 'undefined') return new CloseEvent('close', { code, reason, wasClean });
  return { type: 'close', code, reason, wasClean } as unknown as CloseEvent;
}

function makeErrorEvent(message: string): ErrorEvent {
  if (typeof ErrorEvent !== 'undefined') return new ErrorEvent('error', { message });
  return { type: 'error', message } as unknown as ErrorEvent;
}

/** Open a socket to an already-built subscribe URL (exported for the harness, which injects fake deps). */
export function startStdbSocket(url: string, protocols: string[], deps: StdbSocketDeps = defaultDeps()): StdbSocketAdapter {
  return new StdbSocketAdapter(url, protocols, deps);
}

/** The `WebSocketFactory` for `withWSFn` — the SDK's openWebSocket with the socket in a worker. */
export async function openStdbSocket(args: StdbSocketArgs): Promise<StdbSocketAdapter> {
  const tok = args.authToken ? await fetchWsToken(args.url, args.authToken) : undefined;
  return startStdbSocket(subscribeUrl(args, tok), args.wsProtocol);
}

if (typeof globalThis !== 'undefined') {
  (globalThis as Record<string, unknown>).__stdbSocket = () => (current ? { ...current.stats } : null);
}
